import { AddClientResponse } from "@shared/types";
import { FC, PropsWithChildren, useMemo, useRef, useState } from "react";
import { openMessage } from "utils/misc";
import Link from "common/Typography/Link";
import Text from "common/Typography/Text";
import styled from "styled-components";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import useQuery from "utils/hooks/useQuery";
import {
  CREATE_ACCOUNT_PATH,
  LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY,
  PORTFOLIO_ACCOUNTS_PATH,
} from "common/constants";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import useQuickConnectProvider from "@shared/features/onboarding/hooks/useQuickConnectProvider";
import { BINANCE_FUTURES_LINK } from "@shared/common/constants";
import { getDisplayUrl } from "@shared/features/onboarding/utils";
import { getRouteWithExchange } from "utils/routes";
import TgQuickConnectSuccessModal from "features/configuration/accounts/CreateAccount/TgQuickConnectSuccessModal";
import { usePollClientsList } from "utils/hooks/misc";

interface QuickConnectWrapperProps {
  onSubmitSuccess?: () => void;
  isOnboarding?: boolean;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
  getErrorNotification?: (description: string, title?: string) => void;
  onCreateAccessToken?: (primaryExchange: string) => void;
}

const QuickConnectProvider: FC<PropsWithChildren<QuickConnectWrapperProps>> = ({
  children,
  onSubmitSuccess,
  isOnboarding,
  getSuccessMessage,
  getErrorNotification,
  onCreateAccessToken: propsOnCreateAccessToken,
}) => {
  const [tgQuickConnectModalOpen, setTgQuickConnectModalOpen] = useState(false);
  const { startPolling, pollingAccountCreated } = usePollClientsList();
  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const queryCode = query.get("conn_code");
  const queryState = query.get("state");
  const queryExchanges = query.get("exchanges_list");
  const isPortfolioCreateAccountPath = !!useRouteMatch(CREATE_ACCOUNT_PATH);
  const isPortfolioCreateAccountPathRef = useRef<boolean>();
  isPortfolioCreateAccountPathRef.current = isPortfolioCreateAccountPath;

  const queryData = {
    statusCode: query.get("status_code"),
    failMessage: query.get("fail_message"),
    accountBaseName: query.get("client_base_name"),
    exchangesList: queryExchanges,
  };

  const onCreateAccessToken = (primaryExchange: string) => {
    history.push(getRouteWithExchange(primaryExchange, location));
  };

  const {
    noFuturesAccountMessageSharedData,
    quickConnectAlreadyUsedMessageSharedData,
    primaryExchange,
    closeDrawer,
    tryAgainLoading,
    isLoading,
    quickConnectLoading,
    onBackToSelectExchange,
    isInitialized,
    quickConnectSucceeded,
    code,
    state,
  } = useQuickConnectProvider({
    openMessage,
    queryCode,
    queryState,
    queryData,
    onInitializeCodeAndState: () =>
      history.replace({
        pathname: location.pathname,
        search: "",
      }),
    isOnboarding,
    onOpenModal: () => history.replace(CREATE_ACCOUNT_PATH),
    onCloseModal: () => {
      if (isPortfolioCreateAccountPathRef.current) {
        history.replace(PORTFOLIO_ACCOUNTS_PATH);
      }
    },
    onSubmitSuccess: () => {
      onSubmitSuccess?.();
      const storageValue = localStorage.getItem(
        LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY
      );
      if (storageValue) {
        const parsedStartTime = parseInt(storageValue);
        if (performance.now() - parsedStartTime < 1000 * 60 * 10) {
          setTgQuickConnectModalOpen(true);
        }
        localStorage.removeItem(LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY);
      }
    },
    getSuccessMessage,
    getErrorNotification,
    onCreateAccessToken: propsOnCreateAccessToken ?? onCreateAccessToken,
    pollingAccountCreated,
  });

  const getNoFuturesAccountMessageData = () => {
    const data = noFuturesAccountMessageSharedData;
    return {
      ...data,
      displayMessage: data.show && (
        <MessageWrapper>
          <Text type="danger" style={{ textAlign: "left" }}>
            {data.errorMessage}
          </Text>
          <Text style={{ textAlign: "left" }}>
            Navigate to{" "}
            <Link href={BINANCE_FUTURES_LINK} target="_blank">
              Binance.com/en/futures
            </Link>{" "}
            and create a futures account or unselect the futures sub-exchanges.
          </Text>
        </MessageWrapper>
      ),
    };
  };

  const getQuickConnectAlreadyUsedMessageData = () => {
    const data = quickConnectAlreadyUsedMessageSharedData;
    return {
      ...data,
      displayMessage: data.show && (
        <MessageWrapper>
          <Text type="danger" style={{ textAlign: "left" }}>
            {data.errorMessage}
          </Text>
          <ListWrapper>
            <Text>
              1. Head to{" "}
              <Link href={data.apiManagementLink} target="_blank">
                {getDisplayUrl(data.apiManagementLink)}
              </Link>
            </Text>
            <Text>
              2. Delete the API keys labeled <Bold>Cornix-API</Bold>
            </Text>
            <Text>3. Try to connect to {primaryExchange} again</Text>
          </ListWrapper>
        </MessageWrapper>
      ),
    };
  };

  const contextValue = useMemo(() => {
    return {
      noFuturesAccountMessageData: getNoFuturesAccountMessageData(),
      quickConnectAlreadyUsedMessageData:
        getQuickConnectAlreadyUsedMessageData(),
      closeDrawer,
      tryAgainLoading,
      isLoading,
      quickConnectLoading,
      onBackToSelectExchange,
      isInitialized,
      quickConnectSucceeded,
      code,
      state,
      startAccountsPolling: startPolling,
    };
  }, [
    tryAgainLoading,
    isLoading,
    quickConnectLoading,
    quickConnectAlreadyUsedMessageSharedData,
    noFuturesAccountMessageSharedData,
    isInitialized,
    quickConnectSucceeded,
    code,
    state,
  ]);

  return (
    <QuickConnectContext.Provider value={contextValue}>
      {children}
      <TgQuickConnectSuccessModal
        isOpen={tgQuickConnectModalOpen}
        closeModal={() => setTgQuickConnectModalOpen(false)}
      />
    </QuickConnectContext.Provider>
  );
};

export default QuickConnectProvider;

const Bold = styled.span`
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
`;
const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 16px;
  margin-top: 8px;
  border-left: 2px solid ${(p) => p.theme.colors.red};
  text-align: left;
`;
const MessageWrapper = styled.div`
  margin-bottom: 8px;
  align-self: flex-start;
`;
